/**
 * NavigationSettings Types & Interfaces
 *
 * Single responsibility: Define all types for navigation configuration
 * No business logic, no state management - pure type definitions
 */

/**
 * Fallback strategy when main filters are too restrictive
 */
export type FallbackStrategy = 'less-restrictive' | 'unrestricted-2d' | 'none'

/**
 * Filter application result - tracks which filters rejected candidates
 */
export interface FilterResult {
  validFrames: number[]           // Frame indices that passed all filters
  rejectionReasons: Map<number, string[]>  // frameIndex → reasons why rejected
  filtersApplied: {
    connectivity: boolean
    threeDistance: boolean
    deltaAngle: boolean
    maxCandidates: boolean
  }
}

/**
 * Complete navigation settings configuration
 * User-adjustable parameters for frame selection behavior
 */
export interface NavigationSettings {
  // Distance threshold: percentage of frame width (0-100%)
  distanceThresholdPercent: number

  // Connectivity filtering: only select frames connected to current frame
  useConnectivityFiltering: boolean

  // 3D distance filtering: only select frames within max 3D distance
  use3DDistanceFilter: boolean
  max3DDistance: number  // units from offsetMap (typically 0-3.0)

  // Delta angle filtering: only select frames with similar camera angle
  useDeltaAngleFilter: boolean
  maxDeltaAngle: number  // degrees (typically 0-180)

  // Max candidates: limit candidate pool (null = no limit)
  maxCandidates: number | null

  // Zoom delta: maximum dZ between current frame and next frame
  maxDz: number  // zoom level change (typically 0-5)

  // Fallback strategy when filters are too strict
  fallbackStrategy: FallbackStrategy

  // Sticky frame selection: keep selected frame until red dot exits circular tolerance
  useStickyFrameSelection: boolean

  // Sticky ratio: circular tolerance radius as multiplier of minimum frame dimension
  // (0.5 = half size, 1.0 = full size, 2.0 = double size)
  // Uses circular distance for equal tolerance in all directions regardless of aspect ratio
  stickyRatio: number

  // Enable frame selection during pan (not just at pan end)
  enablePanSelection: boolean

  // Enable directional pan selection: allow sequential frame navigation based on pan motion direction
  // When enabled, allows selecting n±1, n±2, n±3 frames even within sticky bounds if pan motion is clear
  enableDirectionalPanSelection: boolean

  // Show frame boundaries and labels: display borders and frame index labels
  showFrameBoundariesAndLabels: boolean

  // Show extra context frames: display additional frames for spatial context when zoomed out
  showExtraContextFrames: boolean

  // Enable auto-return animation: animate viewport to center selected frame under red dot after pan ends
  enableAutoReturn: boolean

  // Animation settings: control frame animation during navigation
  enablePanMoveAnimation: boolean    // Show preview animation during pan-move
  enablePanEndAnimation: boolean     // Show smooth animation on pan-end (when jumping > 1 frame)

  // NEW: Universal window-based filtering for temporal navigation
  // Applied to both COLMAP and phase correlation shops
  // Limits visible frames by distance from current frame
  sequentialWindow: number              // ±N frames for immediate neighbors (default: 5)
  nearWindow: number                    // ±N frames for extended reach (default: 10)
  contextualWindow: number              // ±N frames absolute maximum (default: 15)

  // Enforce maximum jump distance during panning (phase correlation mode only)
  // When enabled, prevents auto-selection from jumping more than nearWindow frames
  enforceMaxJumpDistance: boolean       // Default: false

  // Progressive weakening and hysteresis: enable shrinking sticky bounds after failed pans
  // and hysteresis to prevent oscillation during frame selection
  useProgressiveWeakeningAndHysteresis: boolean
}

/**
 * Default navigation settings
 * Used when loading app or resetting to defaults
 */
export const DEFAULT_NAVIGATION_SETTINGS: NavigationSettings = {
  distanceThresholdPercent: 50,         // 50% of frame width (225px for 450px)
  useConnectivityFiltering: false,      // ✅ FALSE for phase correlation - use sequential window instead
  use3DDistanceFilter: false,           // Don't filter by 3D distance initially
  max3DDistance: 1.7,                   // Default from offsetMap filtering
  useDeltaAngleFilter: true,            // ✅ Filter by angle - prevents jumping through barriers at U-turns
  maxDeltaAngle: 45,                    // 45 degree tolerance
  maxCandidates: null,                  // No limit on candidates
  maxDz: 1.0,                           // 1.0 zoom delta threshold
  fallbackStrategy: 'less-restrictive',  // ✅ Relax filters progressively when all candidates rejected
  useStickyFrameSelection: false,        // Default to nearest-center selection
  stickyRatio: 1.0,                      // Default to full min dimension for circular tolerance
  enablePanSelection: true,              // Default to selection during pan for responsive feedback
  enableDirectionalPanSelection: true,   // ✅ Enable smooth directional pan navigation by default
  showFrameBoundariesAndLabels: false,   // Default to hiding frame boundaries and labels for cleaner view
  showExtraContextFrames: true,          // Show extra context frames for better spatial awareness
  enableAutoReturn: true,                // Default to auto-return animation after pan ends
  enablePanMoveAnimation: true,          // Show preview animation during pan-move
  enablePanEndAnimation: true,           // Show smooth animation on pan-end (when jumping > 1 frame)
  sequentialWindow: 5,                   // ±5 frames for immediate neighbors
  nearWindow: 50,                        // ±50 frames for extended reach (phase correlation can handle)
  contextualWindow: 127,                 // ±127 frames absolute maximum (full video length)
  enforceMaxJumpDistance: false,         // ✅ FALSE - Allow all sequential frame navigation
  useProgressiveWeakeningAndHysteresis: true, // Enable progressive weakening and hysteresis
}

/**
 * Frame metadata needed for filtering decisions
 * Extracted from offsetMap for each frame
 */
export interface FrameFilterMetadata {
  frameIndex: number
  distance3d?: number           // 3D distance in COLMAP space
  angleDiff?: number            // Camera angle difference (degrees)
  isConnected?: boolean         // Is connected to current frame
}

/**
 * Selection debug info - explain why a frame was selected
 */
export interface SelectionDebugInfo {
  selectedFrame: number
  reason: string                       // Why selected (e.g., "Under red dot")
  confidence: number                   // 0.0-1.0 confidence score
  distance2d: number                   // 2D distance from red dot (pixels)
  distance3d?: number                  // 3D distance if available
  angleDiff?: number                   // Angle difference if available
  hasChanged: boolean                  // Did selection differ from current
  filtersUsed: string[]                // Which filters were applied
  fallbackApplied?: boolean            // Was fallback strategy needed
  fallbackReason?: string              // Why fallback was triggered
}

/**
 * Fallback degradation order
 * When filters are too restrictive, try these in order
 */
export const FALLBACK_DEGRADATION_ORDER: (keyof NavigationSettings)[] = [
  'useDeltaAngleFilter',        // 1. Disable delta angle filter first
  'use3DDistanceFilter',        // 2. Then disable 3D distance filter
  'useConnectivityFiltering',   // 3. Then disable connectivity filter
  // After these, 'unrestricted-2d' fallback takes over
]

/**
 * Validation rules for settings values
 */
export const SETTINGS_CONSTRAINTS = {
  distanceThresholdPercent: { min: 0, max: 500 },
  max3DDistance: { min: 0, max: 10 },
  maxDeltaAngle: { min: 0, max: 180 },
  maxCandidates: { min: 1, max: null as number | null },
  maxDz: { min: 0, max: 5 },
  stickyRatio: { min: 0.1, max: 2.0 },
  sequentialWindow: { min: 1, max: 50 },  // ±1 to ±50 frames
  nearWindow: { min: 5, max: 200 },       // ±5 to ±200 frames
  contextualWindow: { min: 10, max: 500 }, // ±10 to ±500 frames (hard limit)
}

/**
 * Human-readable labels for UI display
 */
export const SETTINGS_LABELS = {
  distanceThresholdPercent: 'Distance Threshold (% of frame width)',
  useConnectivityFiltering: 'Limit to Connected Frames',
  use3DDistanceFilter: 'Filter by 3D Distance',
  max3DDistance: 'Max 3D Distance (units)',
  useDeltaAngleFilter: 'Filter by Camera Angle',
  maxDeltaAngle: 'Max Angle Difference (°)',
  maxCandidates: 'Max Candidate Frames',
  maxDz: 'Max Zoom Delta (dZ)',
  fallbackStrategy: 'Fallback Strategy',
  useStickyFrameSelection: 'Use Sticky Bounds Selection',
  stickyRatio: 'Sticky Bounds Ratio',
  enablePanSelection: 'Select Frames During Pan',
  enableDirectionalPanSelection: 'Directional Pan Selection',
  showFrameBoundariesAndLabels: 'Show Frame Boundaries and Labels',
  showExtraContextFrames: 'Show Extra Context Frames',
  enableAutoReturn: 'Enable Auto-Return Animation',
  enablePanMoveAnimation: 'Pan-Move Preview Animation',
  enablePanEndAnimation: 'Pan-End Smooth Animation',
  sequentialWindow: 'Sequential Window (±frames)',
  nearWindow: 'Near Window (±frames)',
  contextualWindow: 'Contextual Window (±frames)',
  enforceMaxJumpDistance: 'Enforce Max Jump Distance (Sequential Mode)',
  useProgressiveWeakeningAndHysteresis: 'Use Progressive Weakening & Hysteresis',
}

/**
 * Fallback strategy descriptions for UI
 */
export const FALLBACK_STRATEGY_DESCRIPTIONS: Record<FallbackStrategy, string> = {
  'less-restrictive':
    'Gradually disable filters (angle → 3D → connectivity) until frame found',
  'unrestricted-2d':
    'Ignore all filters, select by 2D distance only',
  'none':
    'Do not select if filters reject all candidates',
}
